import React from "react";
import { Link } from "react-router-dom";
import { Filter, Heart, Lock, MessageCircle, Search, Send } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import MemberAvatar from "@/components/common/MemberAvatar";
import ProgramBadge from "@/components/sponsors/ProgramBadge";
import AvailabilityPill, { PresencePill } from "@/components/sponsors/AvailabilityPill";

/**
 * "See How Sponsor Me Works" - product visualisation for the homepage.
 *
 * WHY THIS IS HAND-BUILT RATHER THAN SCREENSHOTTED
 * The panels below are assembled from the app's OWN components and design
 * tokens (MemberAvatar, ProgramBadge, AvailabilityPill, the sm-card surface),
 * so the visitor sees what Sponsor Me genuinely looks like - not stock imagery
 * and not a mock-up that will drift from the product.
 *
 * PRIVACY / HONESTY CONTRACT (do not widen)
 *  - Every value on this page is hard-coded in DEMO_* below. Nothing here is
 *    fetched from the API or the database, so no real member's profile, photo,
 *    location, recovery history or private message can ever appear.
 *  - These illustrative people do not exist: no accounts were created for them,
 *    they are not in the directory, they cannot be contacted, and they are not
 *    counted in any sponsor total shown anywhere on the site.
 *  - Each panel is labelled "Example" and the caption repeats it, so a visitor
 *    cannot mistake a demo card for an available sponsor.
 *  - No outcome, testimonial, rating or success claim appears in this section.
 */
const DEMO_NOTE =
  "Example screens built with the real Sponsor Me interface. The people shown are illustrative only - they are not members, are not listed in the directory, cannot be contacted, and are not included in any sponsor count.";

const DEMO_SPONSORS = [
  {
    id: "example-a",
    user: { display_name: "Example sponsor A" },
    program: "AA",
    presence: "online",
    availability: "accepting",
    years: 7,
    sponsoring: 3,
    location: "Example City, EX",
    about:
      "Illustrative profile text. Sponsors describe their own recovery pathway, how often they check messages, and what sponsorship looks like for them - some help sponsees work the Steps in a 12-step fellowship, others offer accountability and lived experience.",
  },
  {
    id: "example-b",
    user: { display_name: "Example sponsor B" },
    program: "SMART",
    presence: "recently_active",
    availability: "limited",
    years: 4,
    sponsoring: 1,
    location: "Example Town, EX",
    about:
      "Illustrative profile text. A sponsor using a non-12-step pathway might describe the tools they personally use, how much time they have to give back, and the kind of peer support they feel able to offer.",
  },
];

const DEMO_MESSAGES = [
  { id: "m1", mine: true, body: "Thanks for accepting. I'm two weeks in and honestly still shaky." },
  {
    id: "m2",
    mine: false,
    body: "That sounds about right for two weeks - I remember it well. What does a normal day look like for you?",
  },
  { id: "m3", mine: true, body: "Meetings twice a week. Evenings are the hardest part." },
];

const TABS = [
  { value: "browse", label: "1. Browse", icon: Search },
  { value: "profile", label: "2. Read a profile", icon: Heart },
  { value: "request", label: "3. Request", icon: Send },
  { value: "messages", label: "4. Connect", icon: MessageCircle },
];

/** Faux app chrome so each panel reads as a screen, not a decorative card. */
function Screen({ route, children, testid }) {
  return (
    <div
      data-testid={testid}
      className="relative rounded-2xl border border-[#E3DED4] bg-[#F7F4EF] overflow-hidden shadow-[0_18px_40px_-28px_rgba(31,42,46,0.45)]"
    >
      <div className="flex items-center gap-2 px-4 py-2.5 bg-white border-b border-[#E3DED4]">
        <span className="flex gap-1.5" aria-hidden="true">
          <span className="w-2.5 h-2.5 rounded-full bg-[#E3DED4]" />
          <span className="w-2.5 h-2.5 rounded-full bg-[#E3DED4]" />
          <span className="w-2.5 h-2.5 rounded-full bg-[#E3DED4]" />
        </span>
        <span className="ml-1 inline-flex items-center gap-1.5 rounded-full bg-[#F7F4EF] border border-[#E3DED4] px-2.5 py-0.5 text-xs text-[#4B5A60] font-medium truncate">
          <Lock className="w-3 h-3 text-[#1F6F78]" aria-hidden="true" />
          sponsorme<span className="text-[#7A868B]">{route}</span>
        </span>
        <span className="ml-auto shrink-0 rounded-full bg-[#F6E2D6] border border-[#E8C4AC] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-[#8A4B22]">
          Example
        </span>
      </div>
      <div className="p-4 sm:p-5">{children}</div>
    </div>
  );
}

function DemoSponsorCard({ s, compact = false }) {
  return (
    <article
      data-testid={`demo-sponsor-card-${s.id}`}
      className="sm-card p-4 bg-white flex flex-col gap-3"
      aria-label={`Illustrative example sponsor card, ${s.user.display_name}`}
    >
      <div className="flex items-start gap-3">
        <MemberAvatar user={s.user} size={44} />
        <div className="min-w-0">
          <p className="font-display text-base font-bold text-[#1F2A2E] truncate">
            {s.user.display_name}
          </p>
          <div className="mt-1 flex flex-wrap items-center gap-2">
            <ProgramBadge program={s.program} />
            <PresencePill presence={s.presence} />
          </div>
        </div>
      </div>
      <p className="text-sm text-[#4B5A60]">
        <span className="font-semibold text-[#1F2A2E]">{s.years} years in recovery</span>
        {" · "}
        {s.sponsoring} yrs sponsoring
      </p>
      <AvailabilityPill status={s.availability} />
      {!compact && (
        <blockquote className="text-sm text-[#4B5A60] leading-6 border-l-2 border-[#D7EEF0] pl-3 line-clamp-3">
          {s.about}
        </blockquote>
      )}
      <div className="flex gap-2 pt-1">
        <span className="flex-1 h-9 rounded-xl border border-[#1F6F78] text-[#1F6F78] text-sm font-semibold flex items-center justify-center bg-white">
          View Profile
        </span>
        <span className="flex-1 h-9 rounded-xl bg-[#1F6F78] text-white text-sm font-semibold flex items-center justify-center">
          Message
        </span>
      </div>
    </article>
  );
}

export default function ProductTour() {
  const [a, b] = DEMO_SPONSORS;

  return (
    <section
      data-testid="home-product-tour"
      aria-labelledby="product-tour-heading"
      className="max-w-7xl mx-auto px-4 py-14 lg:py-16"
    >
      <div className="max-w-3xl">
        <h2
          id="product-tour-heading"
          data-testid="product-tour-heading"
          className="font-display text-2xl sm:text-3xl font-bold text-[#1F2A2E]"
        >
          See How Sponsor Me Works
        </h2>
        <p className="mt-3 text-[#4B5A60] leading-8">
          A look at the actual screens: browse volunteer peer sponsors, read how someone describes
          their recovery and their approach, request a connection, and - if they accept - keep
          talking in one private conversation.
        </p>
      </div>

      <Tabs defaultValue="browse" className="mt-8">
        <TabsList
          data-testid="product-tour-tabs"
          className="grid w-full grid-cols-2 sm:grid-cols-4 gap-1.5 h-auto bg-[#F2EFE8] p-1.5 rounded-2xl"
        >
          {TABS.map((t) => (
            <TabsTrigger
              key={t.value}
              value={t.value}
              data-testid={`product-tour-tab-${t.value}`}
              className="rounded-xl min-h-[44px] py-2.5 text-xs sm:text-sm font-semibold text-[#4B5A60] touch-manipulation data-[state=active]:bg-white data-[state=active]:text-[#1F6F78] data-[state=active]:shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#2A8C97]"
            >
              <t.icon className="w-3.5 h-3.5 mr-1.5 hidden sm:inline" aria-hidden="true" />
              {t.label}
            </TabsTrigger>
          ))}
        </TabsList>

        {/* 1. Browsing the directory */}
        <TabsContent value="browse" className="mt-5">
          <Screen route="/sponsors" testid="product-tour-screen-browse">
            <div className="rounded-2xl bg-white border border-[#E3DED4] p-3 flex flex-col sm:flex-row gap-2">
              <span className="flex-1 inline-flex items-center gap-2 rounded-xl border border-[#E3DED4] px-3 py-2 text-sm text-[#7A868B]">
                <Search className="w-4 h-4" aria-hidden="true" /> Search names or introductions
              </span>
              <span className="inline-flex items-center gap-2 rounded-xl border border-[#E3DED4] px-3 py-2 text-sm text-[#4B5A60]">
                All programs
              </span>
              <span className="inline-flex items-center gap-2 rounded-xl border border-[#E3DED4] px-3 py-2 text-sm text-[#4B5A60]">
                <Filter className="w-4 h-4" aria-hidden="true" /> More filters
              </span>
            </div>
            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              <DemoSponsorCard s={a} />
              <DemoSponsorCard s={b} />
            </div>
          </Screen>
          <p className="mt-3 text-sm text-[#4B5A60] leading-6">
            Filter by fellowship or pathway, availability, location and how someone prefers to
            communicate. Browsing needs no account and never costs anything.
          </p>
        </TabsContent>

        {/* 2. Reading a sponsor profile */}
        <TabsContent value="profile" className="mt-5">
          <Screen route="/sponsors/example-a" testid="product-tour-screen-profile">
            <div className="rounded-2xl bg-white border border-[#E3DED4] p-5">
              <div className="flex items-start gap-4">
                <MemberAvatar user={a.user} size={64} />
                <div className="min-w-0">
                  <h3 className="font-display text-xl font-bold text-[#1F2A2E]">
                    {a.user.display_name}
                  </h3>
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    <ProgramBadge program={a.program} size="lg" showFull />
                    <AvailabilityPill status={a.availability} />
                  </div>
                  <p className="mt-2 text-sm text-[#4B5A60]">
                    {a.years} years in recovery · {a.sponsoring} yrs sponsoring · {a.location}
                  </p>
                </div>
              </div>
              <div className="mt-5 grid gap-4 sm:grid-cols-2">
                <div className="rounded-xl bg-[#FBF9F5] border border-[#E3DED4] p-4">
                  <p className="text-xs font-bold uppercase tracking-wide text-[#7A868B]">
                    About this sponsor
                  </p>
                  <p className="mt-2 text-sm text-[#4B5A60] leading-6">{a.about}</p>
                </div>
                <div className="rounded-xl bg-[#FBF9F5] border border-[#E3DED4] p-4">
                  <p className="text-xs font-bold uppercase tracking-wide text-[#7A868B]">
                    Sponsorship approach
                  </p>
                  <ul className="mt-2 space-y-1.5 text-sm text-[#4B5A60]">
                    <li>Prefers messaging first, calls later</li>
                    <li>Checks messages most evenings</li>
                    <li>Open to sponsees new to recovery</li>
                  </ul>
                </div>
              </div>
              <div className="mt-5 flex flex-col sm:flex-row gap-2">
                <span className="h-10 px-5 rounded-xl bg-[#1F6F78] text-white text-sm font-semibold inline-flex items-center justify-center">
                  Request sponsorship
                </span>
                <span className="h-10 px-5 rounded-xl border border-[#E3DED4] bg-white text-[#4B5A60] text-sm font-semibold inline-flex items-center justify-center">
                  Save for later
                </span>
              </div>
            </div>
          </Screen>
          <p className="mt-3 text-sm text-[#4B5A60] leading-6">
            Everything on a profile is written by that sponsor themselves. Sponsor Me does not verify
            or vet it, so read carefully and go at your own pace.
          </p>
        </TabsContent>

        {/* 3. Sending a sponsorship request */}
        <TabsContent value="request" className="mt-5">
          <Screen route="/sponsors/example-a" testid="product-tour-screen-request">
            <div className="rounded-2xl bg-white border border-[#E3DED4] p-5 max-w-xl mx-auto">
              <h3 className="font-display text-lg font-bold text-[#1F2A2E]">
                Request sponsorship
              </h3>
              <p className="mt-1.5 text-sm text-[#4B5A60] leading-6">
                Send a short note. {a.user.display_name} can accept or decline - nothing opens until
                they accept.
              </p>
              <div className="mt-4 rounded-xl border border-[#E3DED4] bg-[#FBF9F5] p-3 text-sm text-[#4B5A60] leading-6">
                Example note: "Hi - I'm looking for a sponsor and your approach sounds like a fit.
                I'm early in recovery and trying to build some accountability."
              </div>
              <div className="mt-4 flex items-center gap-2">
                <span className="h-10 px-5 rounded-xl bg-[#1F6F78] text-white text-sm font-semibold inline-flex items-center gap-2">
                  <Send className="w-4 h-4" aria-hidden="true" /> Send request
                </span>
                <span className="text-xs text-[#7A868B]">No unwanted messages either way.</span>
              </div>
              <div className="mt-4 rounded-xl border border-[#E3DED4] bg-white p-3 flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-[#B7791F]" aria-hidden="true" />
                <p className="text-sm text-[#4B5A60]">Request pending - waiting on a reply</p>
              </div>
            </div>
          </Screen>
          <p className="mt-3 text-sm text-[#4B5A60] leading-6">
            Requests are not guaranteed to be accepted. Sponsors are volunteers, set their own
            availability, and may decline or not reply - you are always free to reach out to someone
            else.
          </p>
        </TabsContent>

        {/* 4. Messaging after acceptance */}
        <TabsContent value="messages" className="mt-5">
          <Screen route="/messages" testid="product-tour-screen-messages">
            <div className="rounded-2xl bg-white border border-[#E3DED4] overflow-hidden max-w-xl mx-auto">
              <div className="flex items-center gap-3 px-4 py-3 border-b border-[#E3DED4]">
                <MemberAvatar user={a.user} size={36} />
                <div className="min-w-0">
                  <p className="font-semibold text-sm text-[#1F2A2E] truncate">
                    {a.user.display_name}
                  </p>
                  <PresencePill presence={a.presence} />
                </div>
                <span className="ml-auto text-xs font-semibold text-[#1F7A4D]">Connected</span>
              </div>
              <div className="p-4 space-y-3 bg-[#FBF9F5]">
                {DEMO_MESSAGES.map((m) => (
                  <p
                    key={m.id}
                    className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm leading-6 ${
                      m.mine
                        ? "ml-auto bg-[#1F6F78] text-white"
                        : "bg-white border border-[#E3DED4] text-[#1F2A2E]"
                    }`}
                  >
                    {m.body}
                  </p>
                ))}
              </div>
              <div className="px-4 py-3 border-t border-[#E3DED4] flex items-center gap-2">
                <span className="flex-1 rounded-xl border border-[#E3DED4] px-3 py-2 text-sm text-[#7A868B]">
                  Write a message
                </span>
                <span className="w-9 h-9 rounded-xl bg-[#1F6F78] text-white flex items-center justify-center">
                  <Send className="w-4 h-4" aria-hidden="true" />
                </span>
              </div>
            </div>
          </Screen>
          <p className="mt-3 text-sm text-[#4B5A60] leading-6">
            Conversations stay inside Sponsor Me, so you never have to hand over a phone number to
            start talking. Block and Report sit in every conversation and are always free.
          </p>
        </TabsContent>
      </Tabs>

      <p
        data-testid="product-tour-demo-note"
        className="mt-6 rounded-2xl bg-[#F2EFE8] border border-[#E3DED4] p-4 text-sm text-[#4B5A60] leading-6"
      >
        {DEMO_NOTE}{" "}
        <Link
          to="/sponsors"
          data-testid="product-tour-directory-link"
          className="font-semibold text-[#1F6F78] hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#1F6F78]"
        >
          See the real directory
        </Link>
        .
      </p>
    </section>
  );
}
